import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';

// Shared building blocks for the Dashboard tab modules (Attendance + Red Flag):
// a centred modal, a search box, a generic stacked-bar chart, and number/date
// formatters. Keeps the two tabs visually and behaviourally consistent.

export const fmtCount = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(1));

// A text COLOUR utility — text-gray-700, text-white, text-[#b45309], each with or without
// an opacity (text-gray-700/80). Not a size or an alignment (text-sm, text-[11px],
// text-right), not text-opacity-50, and not a variant (hover:text-…), which only applies
// in its own state and so does not compete with the resting colour.
const isTextColour = (cls: string): boolean =>
  /^text-(?:(?!opacity-)[a-z]+-\d{2,3}|white|black|\[(?:#|rgb|hsl|color:)[^\]]*\])(?:\/[\w.[\]%]+)?$/.test(cls);

/**
 * A table cell's classes with its text colour SWAPPED for a highlight, not stacked on it.
 *
 * Each tab's TD / TDR / TDRC cell classes end in text-gray-700. Appending
 * text-amber-700 to one in a className puts two colour utilities on one element, and
 * the one that wins is whichever Tailwind emits LATER in the stylesheet. It emits the
 * utilities of one property in alphabetical order, not in the order they appear in
 * className, so:
 *   • text-amber-* / text-blue-* / text-emerald-* sort BEFORE text-gray-* and lose —
 *     the cell stays grey. This is how the amber "pending" and "not marked" counts on
 *     the Approvals and Training tabs never showed their colour.
 *   • text-rose-* / text-red-* / text-green-* sort after it and win, which is the only
 *     reason the same pattern looked right for the rose cells beside them.
 * Swapping leaves ONE colour on the cell, so the result no longer depends on its name.
 *
 * `tone` may carry other classes too ('font-medium text-rose-700'); when it names no
 * colour the base keeps its own. A falsy tone returns the base unchanged, so a call
 * site reads `tonedCell(TDR, n > 0 && 'text-amber-700')`.
 */
export const tonedCell = (base: string, tone: string | false | null | undefined): string => {
  if (!tone) return base;
  const toneClasses = tone.split(/\s+/).filter(Boolean);
  if (!toneClasses.some(isTextColour)) return [base, ...toneClasses].join(' ');
  return [...base.split(/\s+/).filter((c) => c && !isTextColour(c)), ...toneClasses].join(' ');
};

export function Modal({
  title, onClose, children, maxWidth = 'max-w-4xl', bodyRef,
}: {
  title: string; onClose: () => void; children: React.ReactNode; maxWidth?: string;
  /** The scrolling body, for a modal that swaps views and must keep each one's scroll. */
  bodyRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="absolute inset-0" onClick={onClose} />
      <div className={`relative flex max-h-[85vh] w-full ${maxWidth} flex-col rounded-2xl bg-white shadow-2xl`}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div ref={bodyRef} className="overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none"
      />
    </div>
  );
}

export interface BarSegment {
  key: string;
  label: string;
  hex: string;
}

interface BarDay {
  date: string;
  label: string | number;
}

interface TooltipState {
  x: number;
  y: number;
  date: string;
  label: string;
  lines: string[];
}

/**
 * A date-wise stacked bar chart rendered as a CSS grid (no chart lib). Bars are
 * scaled to the tallest visible day; each segment's height is its share of the
 * day's visible total. Owns its hover tooltip; calls onSegmentClick when a
 * segment is clicked.
 */
export function StackedBarChart<T extends BarDay>({
  days,
  segments,
  visibleKeys,
  getCount,
  getDayTotal,
  onSegmentClick,
  tooltipLines,
  height = 'h-72',
}: {
  days: T[];
  segments: BarSegment[];
  visibleKeys: Set<string>;
  getCount: (day: T, key: string) => number;
  getDayTotal: (day: T) => number;
  onSegmentClick?: (day: T, seg: BarSegment) => void;
  /** Extra tooltip body lines (after the date + segment label). */
  tooltipLines: (day: T, seg: BarSegment, count: number, dayTotal: number) => string[];
  height?: string;
}) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const visibleSegs = segments.filter((s) => visibleKeys.has(s.key));
  // Clamp to >=0 so a stray negative count can't poison the bar scaling.
  const safeCount = (day: T, key: string) => Math.max(0, getCount(day, key));
  let maxVisible = 0;
  for (const d of days) {
    let sum = 0;
    for (const s of visibleSegs) sum += safeCount(d, s.key);
    if (sum > maxVisible) maxVisible = sum;
  }

  return (
    <>
      <div className={`grid ${height} items-end gap-1`} style={{ gridTemplateColumns: `repeat(${days.length}, minmax(8px, 1fr))` }}>
        {days.map((day) => {
          const visibleTotal = visibleSegs.reduce((acc, s) => acc + safeCount(day, s.key), 0);
          const barHeightPct = maxVisible > 0 ? (visibleTotal / maxVisible) * 100 : 0;
          const dayTotal = getDayTotal(day);
          return (
            <div key={day.date} className="flex h-full flex-col justify-end">
              <div className="flex flex-col-reverse" style={{ height: `${barHeightPct}%` }}>
                {visibleSegs.map((s) => {
                  const count = safeCount(day, s.key);
                  if (count <= 0) return null;
                  const segPct = visibleTotal > 0 ? (count / visibleTotal) * 100 : 0;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      className="w-full"
                      style={{ height: `${segPct}%`, backgroundColor: s.hex }}
                      onMouseEnter={(e) =>
                        setTooltip({ x: e.clientX, y: e.clientY, date: day.date, label: s.label, lines: tooltipLines(day, s, count, dayTotal) })
                      }
                      onMouseMove={(e) =>
                        setTooltip({ x: e.clientX, y: e.clientY, date: day.date, label: s.label, lines: tooltipLines(day, s, count, dayTotal) })
                      }
                      onMouseLeave={() => setTooltip(null)}
                      onClick={() => {
                        setTooltip(null);
                        onSegmentClick?.(day, s);
                      }}
                      aria-label={`${day.date} ${s.label}: ${count}`}
                    />
                  );
                })}
              </div>
              <div className="mt-1 text-center text-[10px] text-gray-400">{day.label}</div>
            </div>
          );
        })}
      </div>
      {tooltip &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[120] whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-2 text-[11px] text-white shadow-lg"
            style={{ left: tooltip.x, top: tooltip.y - 12, transform: 'translate(-50%, -100%)' }}
          >
            <div className="font-semibold">{tooltip.date}</div>
            <div>{tooltip.label}</div>
            {tooltip.lines.map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
